import React from 'react';
import PropTypes from 'prop-types';
import { withStateHandlers, compose, lifecycle } from 'recompose';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import BodyClassName from 'react-body-classname';

import { Button } from 'components/core/Button';
import { countAppliedFilters } from 'utils/search';
import { getKeywordConfig } from 'config/nearMeKeywords';
import { INTERNAL_PAGE_TYPES } from 'config/constants';
import './SearchFiltersTray.scss';
import SearchFiltersVariation from '../SearchFiltersVariation/SearchFiltersVariation';

/**
 * Base search param keys that survive a "Clear All".
 * Everything else (minPrice, maxPrice, hasSpecial, averageReview, etc.)
 * is explicitly set to undefined so the upstream pickBy spread in
 * onSearch properly strips old filter values.
 */
const BASE_PARAM_KEYS = [
	'location',
	'type',
	'city',
	'state',
	'latitude',
	'longitude',
	'zip',
	'subPageType',
	'vehicle',
	'pageType',
	'searchType',
	'sort',
];

export const enhance = compose(
	withStateHandlers(({ searchParams }) => ({ filterParams: searchParams }), {
		handleQueryParamChange: (state) => (params) => {
			// SSDR: Always stage filters for Apply button (both mobile and desktop)
			// Merge new params with existing filterParams (don't replace)
			return {
				filterParams: { ...state.filterParams, ...params },
			};
		},
		handleApplyFilters: (state, props) => () => {
			// Set filtered flag based on whether any filters are applied
			// Gateway uses this to determine whether to generate dynamic SEO content
			const hasFilters = countAppliedFilters(state.filterParams) > 0;
			props.onFilter({
				...state.filterParams,
				filtered: hasFilters ? true : undefined,
			});

			// Close tray and apply filters
			if (props.closeFilters) {
				props.closeFilters();
			}
		},
		handleClearAllFilters: (state, props) => () => {
			// Union of filterParams and searchParams ensures we clear all
			// filter keys even if URL changed externally while tray was open.
			// The explicit undefined is critical — when handleApplyFilters
			// calls onSearch, the upstream merge does:
			//   pickBy({ ...searchParams, ...filterValues })
			// If filter keys are simply absent, the old searchParams values
			// survive. Explicit undefined overwrites them, then pickBy strips.
			const allKeys = new Set([
				...Object.keys(state.filterParams),
				...Object.keys(props.searchParams || {}),
			]);

			const clearedParams = {};
			allKeys.forEach((key) => {
				clearedParams[key] = BASE_PARAM_KEYS.includes(key)
					? state.filterParams[key]
					: undefined;
			});

			// On NMP pages, restore all defining params from the keyword config.
			// The API may not echo these back in search.query after a filter
			// apply, so we derive them from subPageType rather than relying on
			// whatever filterParams currently holds. This covers:
			//   - size/amenity NMPs (mini, 24-hour, etc.) → amenity param
			//   - vehicle NMPs (boat, car, rv) → type + vehicle params
			if (
				clearedParams.pageType ===
					INTERNAL_PAGE_TYPES.NEAR_ME_SEARCH_PAGE &&
				clearedParams.subPageType
			) {
				const keywordConfig = getKeywordConfig(
					clearedParams.subPageType,
				);
				if (keywordConfig?.searchParams) {
					Object.assign(clearedParams, keywordConfig.searchParams);

					// Vehicle filter handlers (handleVehicleSizeSelect etc.) write
					// type:'vehicle' and searchType:'vehicle' into filterParams.
					// Both keys are in BASE_PARAM_KEYS so they survive the clear
					// loop above — even after the user has already Applied vehicle
					// filters (at which point props.searchParams also has 'vehicle').
					// For non-vehicle NMPs the keyword config is the only reliable
					// source of truth: if it doesn't set type/searchType, they must
					// be cleared so the search falls back to storage.
					if (keywordConfig.category !== 'vehicle') {
						clearedParams.type = keywordConfig.searchParams.type;
						clearedParams.searchType =
							keywordConfig.searchParams.searchType;
					}
				}
			}

			return {
				filterParams: clearedParams,
			};
		},
		updateParams: () => (filterParams) => ({ filterParams }),
		/**
		 * Discards staged filter changes and closes the tray.
		 * Called when user closes tray without clicking "Apply Filters".
		 * Resets filterParams to searchParams so unapplied selections
		 * don't persist when the tray is reopened (SF-19289).
		 */
		handleCloseWithoutApply: (state, props) => () => {
			if (props.closeFilters) {
				props.closeFilters();
			}
			// Reset staged filters to match currently applied search params
			return {
				filterParams: props.searchParams,
			};
		},
	}),
	lifecycle({
		componentDidUpdate(prevProps) {
			if (!isEqual(prevProps.searchParams, this.props.searchParams)) {
				this.props.updateParams(this.props.searchParams);
			}
		},
	}),
);

export function SearchFiltersTray({
	filtersShowing,
	handleQueryParamChange,
	filterParams,
	handleApplyFilters,
	handleClearAllFilters,
	handleCloseWithoutApply,
	searchType,
	useNewSearch,
	// unitFilters, // TODO: wire up for size/amenity filter sections
	facetCounts,
	className,
	idPrefix = '', // Unique prefix for element IDs to avoid conflicts with multiple instances
}) {
	// Prevent body scroll when mobile tray is open via CSS class (matches Modal pattern)
	// Desktop dropdowns allow background scrolling; only lock scroll for mobile
	const isMobileTray = className === 'mobile-filter-tray';

	return (
		<BodyClassName
			className={classnames({
				'no-scroll': filtersShowing && isMobileTray,
			})}
		>
			<>
				{/* Backdrop overlay - dark on mobile, transparent on desktop for click-outside */}
				{filtersShowing && (
					<button
						type="button"
						className={classnames(
							'filters-tray-backdrop',
							className,
						)}
						onClick={handleCloseWithoutApply}
						aria-label="Close filters"
					/>
				)}
				<div
					className={classnames('search-filters-tray', className, {
						open: filtersShowing,
						'new-search': useNewSearch,
					})}
				>
					<div className="filters-tray-content">
						<div className="modal-header">
							<h2 className="modal-title">Filters</h2>

							<Button
								type="button"
								className="clear-all-button"
								link={true}
								gray={true}
								onClick={handleClearAllFilters}
								underline={true}
							>
								Clear All
							</Button>

							{handleCloseWithoutApply && (
								<button
									onClick={handleCloseWithoutApply}
									className="close-button"
									type="button"
									aria-label="Close filters"
								>
									×
								</button>
							)}
						</div>

						<SearchFiltersVariation
							queryParams={filterParams}
							onFilter={handleQueryParamChange}
							facetCounts={facetCounts}
							searchType={searchType}
							idPrefix={idPrefix}
						/>

						<div className="filters-tray-footer">
							<Button
								onClick={handleApplyFilters}
								primary={!useNewSearch}
								secondary={useNewSearch}
								className="apply-filters-button"
							>
								Apply Filters
							</Button>
						</div>
					</div>
				</div>
			</>
		</BodyClassName>
	);
}

SearchFiltersTray.propTypes = {
	filtersShowing: PropTypes.bool,
	filterParams: PropTypes.object,
	handleQueryParamChange: PropTypes.func,
	handleApplyFilters: PropTypes.func.isRequired,
	handleClearAllFilters: PropTypes.func.isRequired,
	handleCloseWithoutApply: PropTypes.func.isRequired,
	searchType: PropTypes.string,
	useNewSearch: PropTypes.bool,
	searchParams: PropTypes.object,
	onFilter: PropTypes.func,
	facetCounts: PropTypes.object,
	className: PropTypes.string,
	idPrefix: PropTypes.string,
};

export default enhance(SearchFiltersTray);
